import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Edit,
  Trash2,
  Search,
  ToggleLeft,
  ToggleRight,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Plus,
} from 'lucide-react';
import { Dialog } from '@headlessui/react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import debounce from 'lodash.debounce';
import Papa from 'papaparse';
import { updateAllForumDescriptions, resolveForumDescription } from '../../utils/updateForumDescriptions';
import { scrapeForumStats, buildScrapeForumStatsPayload } from '../../utils/scrapeForumStats';
import { buildBulkScrapeCountUpdate, buildPreviousMonthPatch } from '../../utils/forumStatChange';
import { buildForumAutoMaintenancePatch } from '../../utils/forumAutoMaintenance';
import {
  buildBulkScrapeRowResult,
  buildFailedRow,
  buildNoSelectorRow,
  forumHasAnySelector,
  getPendingBulkSaveRows,
  isBulkScrapeRetryEligible,
  type BulkScrapeReport,
  type BulkScrapeRowResult,
  type BulkScrapeScope,
  summarizeBulkScrapeReport,
} from '../../utils/bulkScrapeReport';
import BulkScrapeReportDialog from './BulkScrapeReportDialog';
import BulkScrapeProgressDialog, {
  type BulkProgressState,
  type BulkProgressEntry,
} from './BulkScrapeProgressDialog';
import ForumListToolbar from './ForumListToolbar';
import { formatLastUpdated, formatLastUpdatedAdmin, lastUpdatedSource } from '../../utils/formatLastUpdated';
import { recordForumStatSnapshot } from '../../utils/forumStatSnapshot';
import {
  type SuggestionPrefillPayload,
  suggestionPrefillToForumForm,
} from '../../utils/forumSuggestionPrefill';

function updateBulkProgressEntry(
  progress: BulkProgressState,
  key: string,
  patch: Partial<BulkProgressEntry>,
  meta?: Partial<Pick<BulkProgressState, 'current' | 'currentLabel'>>
): BulkProgressState {
  return {
    ...progress,
    ...meta,
    entries: progress.entries.map((e) => (e.key === key ? { ...e, ...patch } : e)),
  };
}
import { DEFAULT_FORUM_LOGO_URL } from '../../types/forum';
import { createEmptyForumForm, resolveForumLogoUrl } from '../../utils/forumFormDefaults';
import {
  emptyForumAdminRow,
  emptyForumControllerRow,
  normalizeForumAdmins,
  normalizeForumContacts,
  resolveForumAdmins,
  resolveForumControllers,
} from '../../utils/forumAdmins';
import ForumScorePreview from './ForumScorePreview';
import ForumLogoPreview from './ForumLogoPreview';
import { calculateForumScore } from '../../utils/forumScore';

interface Forum {
  id: string;
  forum_id: string;
  name: string;
  url: string;
  category: string;
  topics_count: number;
  posts_count: number;
  members_count: number;
  previous_month_topics_count?: number | null;
  previous_month_posts_count?: number | null;
  previous_month_members_count?: number | null;
  founded_year: number;
  description: string;
  logo_url: string;
  is_active: boolean;
  software: string | null;
  is_sponsored: boolean;
  sponsored_until: string | null;
  user_id: string | null;
  topics_selector?: string | null;
  posts_selector?: string | null;
  members_selector?: string | null;
  scrape_login_url?: string | null;
  scrape_username?: string | null;
  scrape_password?: string | null;
  scrape_login_username_selector?: string | null;
  scrape_login_password_selector?: string | null;
  scrape_login_submit_selector?: string | null;
  promo_section_url?: string | null;
  forum_admins?: { name: string; profile_url: string }[] | null;
  forum_controllers?: { name: string; profile_url: string }[] | null;
  forum_admin_name?: string | null;
  forum_admin_profile_url?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
}

interface Category {
  id: string;
  name: string;
  description: string;
}

interface SortConfig {
  key: keyof Forum;
  direction: 'asc' | 'desc';
}

interface FilterConfig {
  category: string;
  yearRange: [number, number];
  minMembers: number;
  minPosts: number;
}

const ITEMS_PER_PAGE = 100;

type LocationSuggestionState = {
  suggestionPrefill?: SuggestionPrefillPayload;
};

const ForumList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingSuggestionId, setPendingSuggestionId] = useState<string | null>(null);
  const suggestionHandledRef = useRef(false);
  const [forums, setForums] = useState<Forum[]>([]);
  const [filteredForums, setFilteredForums] = useState<Forum[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedForum, setSelectedForum] = useState<Forum | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'name', direction: 'asc' });
  const [filters, setFilters] = useState<FilterConfig>({
    category: '',
    yearRange: [1995, new Date().getFullYear()],
    minMembers: 0,
    minPosts: 0
  });
  const [updatingDescriptions, setUpdatingDescriptions] = useState(false);
  const [scrapingModal, setScrapingModal] = useState(false);
  const [regeneratingDescription, setRegeneratingDescription] = useState(false);
  const [bulkScraping, setBulkScraping] = useState(false);
  const [bulkScrapeReport, setBulkScrapeReport] = useState<BulkScrapeReport | null>(null);
  const [bulkScrapeReportOpen, setBulkScrapeReportOpen] = useState(false);
  const [bulkScrapeSaving, setBulkScrapeSaving] = useState(false);
  const [bulkScrapeScope, setBulkScrapeScope] = useState<BulkScrapeScope>('active');
  const [bulkProgress, setBulkProgress] = useState<BulkProgressState | null>(null);
  /** Scrape sonrası number inputların DOM'da güncellenmesi için (kontrollü input senkron hatası) */
  const [statCountInputNonce, setStatCountInputNonce] = useState(0);
  const storedScrapePasswordRef = useRef<string>('');

  const [formData, setFormData] = useState(createEmptyForumForm);

  const exportData = async (format: 'csv' | 'txt') => {
    try {
      const { data: allForums, error } = await supabase
        .from('forums')
        .select('*')
        .order('name');

      if (error) throw error;

      if (!allForums || allForums.length === 0) {
        toast.error('Dışa aktarılacak veri bulunamadı');
        return;
      }

      const exportFields = [
        'forum_id',
        'name',
        'url',
        'category',
        'topics_count',
        'posts_count',
        'members_count',
        'founded_year',
        'description',
        'is_active',
        'software',
        'created_at'
      ];

      let content: string;
      let filename: string;
      let mimeType: string;

      if (format === 'csv') {
        content = Papa.unparse(allForums.map(forum => {
          const row: any = {};
          exportFields.forEach(field => {
            row[field] = forum[field];
          });
          return row;
        }));
        filename = `forum-list-${new Date().toISOString().split('T')[0]}.csv`;
        mimeType = 'text/csv';
      } else {
        content = allForums.map(forum => {
          return exportFields.map(field => `${field}: ${forum[field] || ''}`).join('\n') + '\n---\n';
        }).join('\n');
        filename = `forum-list-${new Date().toISOString().split('T')[0]}.txt`;
        mimeType = 'text/plain';
      }

      const blob = new Blob([content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Veriler başarıyla dışa aktarıldı');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Veriler dışa aktarılırken bir hata oluştu');
    }
  };

  const handleUpdateDescriptions = async () => {
    if (
      !confirm(
        'Tüm forumların açıklamaları yeniden üretilip kaydedilecek. Mevcut metinlerin üzerine yazılır. Devam edilsin mi?'
      )
    ) {
      return;
    }

    setUpdatingDescriptions(true);
    try {
      const result = await updateAllForumDescriptions();
      await fetchForums();

      if (result.failed > 0 && result.updated === 0) {
        toast.error(`Açıklamalar güncellenemedi (${result.failed} hata)`);
        return;
      }

      if (result.updated === 0) {
        toast.success(`Değişiklik yok: ${result.unchanged} forum zaten güncel`);
        return;
      }

      const unchangedNote =
        result.unchanged > 0 ? `, ${result.unchanged} forum zaten güncel` : '';
      const failedNote = result.failed > 0 ? `, ${result.failed} hata` : '';
      toast.success(`${result.updated} forum güncellendi${unchangedNote}${failedNote}`);
    } catch (error) {
      console.error('Error updating descriptions:', error);
      toast.error('Açıklamalar güncellenirken bir hata oluştu');
    } finally {
      setUpdatingDescriptions(false);
    }
  };

  const getScrapeFormSource = () => ({
    ...formData,
    scrape_password:
      formData.scrape_password?.trim() || storedScrapePasswordRef.current || '',
  });

  const handleScrapeFromSelectorsInModal = async () => {
    if (!formData.url?.trim()) {
      toast.error('Önce forum URL girin');
      return;
    }
    if (
      !formData.topics_selector?.trim() &&
      !formData.posts_selector?.trim() &&
      !formData.members_selector?.trim()
    ) {
      toast.error('En az bir CSS seçici girin');
      return;
    }
    setScrapingModal(true);
    try {
      const result = await scrapeForumStats(supabase, buildScrapeForumStatsPayload(getScrapeFormSource()));
      setFormData((prev) => ({
        ...prev,
        topics_count:
          result.topics_count !== null && result.topics_count !== undefined
            ? result.topics_count
            : prev.topics_count,
        posts_count:
          result.posts_count !== null && result.posts_count !== undefined
            ? result.posts_count
            : prev.posts_count,
        members_count:
          result.members_count !== null && result.members_count !== undefined
            ? result.members_count
            : prev.members_count,
      }));

      const hasAnyNumber =
        result.topics_count !== null ||
        result.posts_count !== null ||
        result.members_count !== null;

      if (hasAnyNumber) {
        setStatCountInputNonce((n) => n + 1);
      }

      const warnText = result.messages?.length
        ? result.messages.join(' · ').replace(/\s+/g, ' ').trim()
        : '';

      const summaryParts: string[] = [];
      if (result.topics_count !== null) {
        summaryParts.push(`Konu: ${result.topics_count.toLocaleString('tr-TR')}`);
      }
      if (result.posts_count !== null) {
        summaryParts.push(`İleti: ${result.posts_count.toLocaleString('tr-TR')}`);
      }
      if (result.members_count !== null) {
        summaryParts.push(`Üye: ${result.members_count.toLocaleString('tr-TR')}`);
      }
      const summary = summaryParts.join(' · ');

      if (result.messages?.length && !hasAnyNumber) {
        toast.error(`Hiçbir sayı okunamadı (alanlar eski kaldı). ${warnText || 'Seçicileri kontrol edin.'}`, {
          duration: 5000,
          style: { maxWidth: 'min(96vw, 56rem)', whiteSpace: 'pre-wrap' },
        });
        console.warn('Scrape uyarıları:', result.messages);
      } else if (result.messages?.length && hasAnyNumber) {
        toast.success(
          `${summary ? `${summary}. ` : ''}Kaydetmeyi unutmayın. Uyarı: ${warnText}`,
          { duration: 4000, style: { maxWidth: 'min(96vw, 56rem)', whiteSpace: 'pre-wrap' } }
        );
        console.warn('Scrape uyarıları:', result.messages);
      } else if (hasAnyNumber) {
        toast.success(
          `${summary ? `${summary} okundu. ` : ''}Kaydetmeyi unutmayın (veritabanı için Güncelle).`,
          { duration: 3000 }
        );
      } else {
        toast.error('Sunucu sayı döndürmedi; seçicileri ve forum URL’sini kontrol edin.', { duration: 4000 });
      }
    } catch (err) {
      const full = err instanceof Error ? err.message : String(err);
      console.error(full);
      toast.error(full, {
        duration: 5000,
        style: { maxWidth: 'min(96vw, 56rem)', whiteSpace: 'pre-wrap' },
      });
    } finally {
      setScrapingModal(false);
    }
  };

  const handleRegenerateDescription = () => {
    if (!formData.forum_id.trim() || !formData.name.trim()) {
      toast.error('Önce forum ID ve adını girin');
      return;
    }

    setRegeneratingDescription(true);
    try {
      const description = resolveForumDescription({
        forum_id: formData.forum_id.trim(),
        name: formData.name.trim(),
        category: formData.category,
        members_count: formData.members_count,
        posts_count: formData.posts_count,
        topics_count: formData.topics_count,
      });
      setFormData((prev) => ({ ...prev, description }));
      toast.success('Açıklama güncellendi — kaydetmeyi unutmayın');
    } finally {
      setRegeneratingDescription(false);
    }
  };

  const scrapeOneForumForBulk = async (forum: Forum): Promise<BulkScrapeRowResult> => {
    try {
      const result = await scrapeForumStats(supabase, buildScrapeForumStatsPayload(forum));
      const countUpdate = buildBulkScrapeCountUpdate(forum, {
        topics_count: result.topics_count,
        posts_count: result.posts_count,
        members_count: result.members_count,
      });
      if (result.messages?.length) {
        console.warn(forum.name, result.messages);
      }
      return buildBulkScrapeRowResult(forum, result, countUpdate);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error(forum.name, e);
      return buildFailedRow(forum, msg);
    }
  };

  const handleBulkScrapeFromSelectors = async () => {
    const pool =
      bulkScrapeScope === 'active' ? forums.filter((f) => f.is_active) : forums;

    const scrapeTargets = pool.filter(
      (f) => f.url?.trim() && forumHasAnySelector(f)
    );
    const noSelectorForums = pool.filter((f) => !forumHasAnySelector(f));

    if (scrapeTargets.length === 0 && noSelectorForums.length === 0) {
      toast.error('Listede forum bulunamadı');
      return;
    }

    const scopeLabel =
      bulkScrapeScope === 'active' ? 'yalnızca aktif forumlar' : 'tüm forumlar (aktif + pasif)';

    const confirmParts: string[] = [`Kapsam: ${scopeLabel}`];
    if (scrapeTargets.length > 0) {
      confirmParts.push(
        `${scrapeTargets.length} forum için dış sitelerden veri çekilecek; sonuçlar önce önizlemede gösterilir, onayladığınızda veritabanına yazılır`
      );
    }
    if (noSelectorForums.length > 0) {
      confirmParts.push(
        `${noSelectorForums.length} forum seçici olmadığı için yalnızca raporda listelenecek`
      );
    }
    if (!confirm(`${confirmParts.join('; ')}. Devam?`)) {
      return;
    }

    setBulkScraping(true);
    const rows: BulkScrapeReport['rows'] = [];
    setBulkProgress({
      phase: 'scrape',
      current: 0,
      total: scrapeTargets.length,
      currentLabel: '',
      entries: scrapeTargets.map((f) => ({ key: f.id, name: f.name, state: 'pending' })),
    });
    let scrapeDone = 0;
    try {
      for (const forum of scrapeTargets) {
        setBulkProgress((p) =>
          p
            ? updateBulkProgressEntry(p, forum.id, { state: 'active' }, { currentLabel: forum.name })
            : p
        );
        const row = await scrapeOneForumForBulk(forum);
        rows.push(row);
        scrapeDone += 1;
        setBulkProgress((p) =>
          p
            ? updateBulkProgressEntry(
                p,
                forum.id,
                {
                  state: row.status === 'failed' ? 'fail' : 'ok',
                  note: row.reason,
                },
                { current: scrapeDone }
              )
            : p
        );
        await new Promise((r) => setTimeout(r, 450));
      }

      const retryForums = scrapeTargets.filter((forum) => {
        const row = rows.find((r) => r.id === forum.id);
        return row != null && isBulkScrapeRetryEligible(row);
      });

      if (retryForums.length > 0) {
        let retryDone = 0;
        let retryRecovered = 0;
        setBulkProgress({
          phase: 'scrape_retry',
          current: 0,
          total: retryForums.length,
          currentLabel: '',
          entries: retryForums.map((f) => ({
            key: f.id,
            name: f.name,
            state: 'pending' as const,
            note: '2. tur bekliyor',
          })),
        });
        await new Promise((r) => setTimeout(r, 1200));

        for (const forum of retryForums) {
          const previousRow = rows.find((r) => r.id === forum.id);
          setBulkProgress((p) =>
            p
              ? updateBulkProgressEntry(
                  p,
                  forum.id,
                  { state: 'active', note: 'Yeniden deneniyor…' },
                  { currentLabel: forum.name }
                )
              : p
          );

          const row = await scrapeOneForumForBulk(forum);
          const rowIndex = rows.findIndex((r) => r.id === forum.id);
          if (rowIndex >= 0) {
            if (previousRow && isBulkScrapeRetryEligible(previousRow) && !isBulkScrapeRetryEligible(row)) {
              retryRecovered += 1;
            }
            rows[rowIndex] = row;
          }

          retryDone += 1;
          setBulkProgress((p) =>
            p
              ? updateBulkProgressEntry(
                  p,
                  forum.id,
                  {
                    state: row.status === 'failed' ? 'fail' : 'ok',
                    note:
                      row.status === 'failed' || row.status === 'skipped'
                        ? `2. tur: ${row.reason}`
                        : previousRow && isBulkScrapeRetryEligible(previousRow)
                          ? '2. turda veri alındı'
                          : row.reason,
                  },
                  { current: retryDone }
                )
              : p
          );
          await new Promise((r) => setTimeout(r, 450));
        }

        if (retryRecovered > 0) {
          console.info(`Toplu scrape 2. tur: ${retryRecovered} forum kurtarıldı`);
        }
      }

      for (const forum of noSelectorForums) {
        rows.push(buildNoSelectorRow(forum));
      }

      rows.sort((a, b) => a.name.localeCompare(b.name, 'tr'));

      const report: BulkScrapeReport = {
        finishedAt: new Date(),
        total: rows.length,
        scope: bulkScrapeScope,
        mode: 'preview',
        rows,
      };
      setBulkScrapeReport(report);
      setBulkScrapeReportOpen(true);

      const { pendingSave, failed, noSelector } = summarizeBulkScrapeReport(report);
      const retriedCount = scrapeTargets.filter((f) => {
        const row = report.rows.find((r) => r.id === f.id);
        return row != null && (row.status === 'failed' || row.status === 'skipped');
      }).length;
      const toastParts = [
        pendingSave ? `${pendingSave} forum kayda hazır` : null,
        failed ? `${failed} hata` : null,
        noSelector ? `${noSelector} seçici olmayan` : null,
        retriedCount > 0 ? `${retriedCount} forum 2. turdan sonra hâlâ başarısız` : null,
      ].filter(Boolean);
      toast.success(
        toastParts.length
          ? `Önizleme hazır: ${toastParts.join(', ')} — kaydetmek için pencereyi inceleyin`
          : 'Önizleme hazır — pencereyi inceleyin',
        { duration: 4000 }
      );
    } finally {
      setBulkScraping(false);
      setBulkProgress(null);
    }
  };

  const handleCloseBulkScrapeReport = () => {
    if (bulkScrapeSaving) return;
    setBulkScrapeReportOpen(false);
    setBulkScrapeReport(null);
  };

  const handleConfirmBulkScrapeSave = async (selectedRowIds?: string[]) => {
    if (!bulkScrapeReport || bulkScrapeReport.mode !== 'preview') return;

    const pendingAll = getPendingBulkSaveRows(bulkScrapeReport);
    const pending =
      selectedRowIds && selectedRowIds.length > 0
        ? pendingAll.filter((r) => selectedRowIds.includes(r.id))
        : pendingAll;
    if (pending.length === 0) {
      toast.error('Kaydedilecek değişiklik yok');
      return;
    }

    if (
      !confirm(
        `${pending.length} forum için okunan sonuçlar kaydedilecek (sayı değişmeyenlerde yalnızca son güncellenme tarihi). Onaylıyor musunuz?`
      )
    ) {
      return;
    }

    setBulkScrapeSaving(true);
    let saved = 0;
    let failed = 0;
    let saveDone = 0;
    const nextRows = [...bulkScrapeReport.rows];
    setBulkProgress({
      phase: 'save',
      current: 0,
      total: pending.length,
      currentLabel: '',
      entries: pending.map((r) => ({ key: r.id, name: r.name, state: 'pending' })),
    });

    try {
      const { data: session } = await supabase
        .from('bulk_update_sessions')
        .insert({ total_forums: pending.length, status: 'running', can_rollback: true })
        .select('id')
        .maybeSingle();

      for (const row of pending) {
        setBulkProgress((p) =>
          p
            ? updateBulkProgressEntry(p, row.id, { state: 'active' }, { currentLabel: row.name })
            : p
        );
        const idx = nextRows.findIndex((r) => r.id === row.id);
        const savedAt = new Date().toISOString();
        const touchOnly = Boolean(row.touchUpdatedAt) && !row.countUpdate;
        const before = forums.find((f) => f.id === row.id);

        let error: { message: string } | null = null;

        if (touchOnly) {
          const touchRes = await supabase
            .from('forums')
            .update({ updated_at: savedAt })
            .eq('id', row.id);
          error = touchRes.error;
        } else {
          const cu = row.countUpdate!;
          const mergedCounts = {
            topics_count: cu.topics_count ?? before?.topics_count ?? 0,
            posts_count: cu.posts_count ?? before?.posts_count ?? 0,
            members_count: cu.members_count ?? before?.members_count ?? 0,
          };
          // Otomatik bakım: eksik önceki-ay bazı + güncel sayılara göre açıklama.
          const autoPatch = before
            ? buildForumAutoMaintenancePatch({
                counts: mergedCounts,
                prevMonth: {
                  previous_month_topics_count:
                    cu.previous_month_topics_count ?? before.previous_month_topics_count ?? null,
                  previous_month_posts_count:
                    cu.previous_month_posts_count ?? before.previous_month_posts_count ?? null,
                  previous_month_members_count:
                    cu.previous_month_members_count ?? before.previous_month_members_count ?? null,
                },
                forum: {
                  forum_id: before.forum_id,
                  name: before.name,
                  category: before.category,
                  ...mergedCounts,
                },
                currentDescription: before.description,
                regenerateDescription: true,
              })
            : {};
          const updateRes = await supabase
            .from('forums')
            .update({ ...cu, ...autoPatch, updated_at: savedAt })
            .eq('id', row.id);
          error = updateRes.error;

          if (!error) {
            await recordForumStatSnapshot(
              supabase,
              { id: row.id, ...mergedCounts },
              savedAt,
              { force: true }
            );
            if (session?.id && before) {
              await supabase.from('bulk_update_results').insert({
                session_id: session.id,
                forum_id: row.id,
                forum_name: row.name,
                old_data: {
                  topics_count: before.topics_count,
                  posts_count: before.posts_count,
                  members_count: before.members_count,
                  previous_month_topics_count: before.previous_month_topics_count,
                  previous_month_posts_count: before.previous_month_posts_count,
                  previous_month_members_count: before.previous_month_members_count,
                },
                new_data: { ...cu, updated_at: savedAt },
              });
            }
          }
        }

        if (!error && touchOnly) {
          if (before) {
            await recordForumStatSnapshot(
              supabase,
              {
                id: row.id,
                topics_count: before.topics_count,
                posts_count: before.posts_count,
                members_count: before.members_count,
              },
              savedAt,
              { force: true }
            );
          }
          if (session?.id && before) {
            await supabase.from('bulk_update_results').insert({
              session_id: session.id,
              forum_id: row.id,
              forum_name: row.name,
              old_data: {
                topics_count: before.topics_count,
                posts_count: before.posts_count,
                members_count: before.members_count,
                previous_month_topics_count: before.previous_month_topics_count,
                previous_month_posts_count: before.previous_month_posts_count,
                previous_month_members_count: before.previous_month_members_count,
              },
              new_data: {
                topics_count: before.topics_count,
                posts_count: before.posts_count,
                members_count: before.members_count,
                updated_at: savedAt,
              },
            });
          }
        }

        saveDone += 1;

        if (idx < 0) {
          setBulkProgress((p) =>
            p
              ? updateBulkProgressEntry(
                  p,
                  row.id,
                  { state: 'fail', note: 'Satır bulunamadı' },
                  { current: saveDone }
                )
              : p
          );
          continue;
        }

        if (error) {
          failed += 1;
          nextRows[idx] = {
            ...nextRows[idx],
            status: 'failed',
            reason: error.message,
            countUpdate: undefined,
            touchUpdatedAt: undefined,
          };
          setBulkProgress((p) =>
            p
              ? updateBulkProgressEntry(
                  p,
                  row.id,
                  { state: 'fail', note: error.message },
                  { current: saveDone }
                )
              : p
          );
        } else {
          saved += 1;
          nextRows[idx] = {
            ...nextRows[idx],
            status: touchOnly ? 'unchanged' : 'updated',
            reason: touchOnly
              ? 'Son güncellenme tarihi kaydedildi (sayılar aynı)'
              : 'Veritabanına kaydedildi',
            countUpdate: undefined,
            touchUpdatedAt: undefined,
            lastUpdatedLabel: formatLastUpdated(savedAt),
          };
          setBulkProgress((p) =>
            p
              ? updateBulkProgressEntry(
                  p,
                  row.id,
                  {
                    state: 'ok',
                    note: touchOnly
                      ? 'Son güncellenme tarihi kaydedildi'
                      : 'Veritabanına kaydedildi',
                  },
                  { current: saveDone }
                )
              : p
          );
        }
      }

      if (session?.id) {
        await supabase
          .from('bulk_update_sessions')
          .update({
            completed_at: new Date().toISOString(),
            successful_updates: saved,
            failed_updates: failed,
            status: failed > 0 && saved === 0 ? 'failed' : 'completed',
          })
          .eq('id', session.id);
      }

      setBulkScrapeReport({
        ...bulkScrapeReport,
        mode: 'committed',
        rows: nextRows,
      });

      if (saved > 0) {
        await fetchForums();
      }

      if (failed > 0) {
        toast.error(`${saved} forum kaydedildi, ${failed} forumda hata oluştu`);
      } else {
        toast.success(`${saved} forum başarıyla güncellendi`);
      }
    } catch (e) {
      console.error(e);
      toast.error('Kayıt sırasında beklenmeyen bir hata oluştu');
    } finally {
      setBulkScrapeSaving(false);
      setBulkProgress(null);
    }
  };

  /** previous_month=0 iken yanlış (+tam sayı) görünmesin diye mevcut sayıları baz yapar; sonraki scrape gerçek farkı gösterir. */
  const handleFillAllSnapshots = async () => {
    if (!confirm('Tüm aktif forumlar için eksik geçmiş kayıtları doldurulsun mu?')) return;
    let n = 0;
    for (const f of forums.filter((x) => x.is_active)) {
      await recordForumStatSnapshot(
        supabase,
        {
          id: f.id,
          topics_count: f.topics_count,
          posts_count: f.posts_count,
          members_count: f.members_count,
        },
        f.updated_at || undefined
      );
      n += 1;
    }
    toast.success(`${n} forum işlendi`);
  };

  const handleRollbackLastBulk = async () => {
    const { data: session } = await supabase
      .from('bulk_update_sessions')
      .select('id, started_at')
      .eq('can_rollback', true)
      .order('started_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!session?.id) {
      toast.error('Geri alınabilir toplu güncelleme oturumu yok');
      return;
    }
    if (!confirm('Son toplu güncelleme geri alınacak. Emin misiniz?')) return;

    const { data: results } = await supabase
      .from('bulk_update_results')
      .select('forum_id, old_data')
      .eq('session_id', session.id);

    if (!results?.length) {
      toast.error('Geri alma kaydı bulunamadı');
      return;
    }

    let ok = 0;
    for (const r of results) {
      const old = r.old_data as Record<string, number>;
      const { error } = await supabase
        .from('forums')
        .update({
          topics_count: old.topics_count,
          posts_count: old.posts_count,
          members_count: old.members_count,
          previous_month_topics_count: old.previous_month_topics_count,
          previous_month_posts_count: old.previous_month_posts_count,
          previous_month_members_count: old.previous_month_members_count,
        })
        .eq('id', r.forum_id);
      if (!error) ok += 1;
    }

    await supabase.from('bulk_update_sessions').update({ can_rollback: false }).eq('id', session.id);
    await fetchForums();
    toast.success(`${ok} forum önceki değerlere döndürüldü`);
  };

  const handleEstablishStatBaselines = async () => {
    const needsBaseline = forums.filter(
      (f) =>
        (f.topics_count > 0 && !(f.previous_month_topics_count ?? 0)) ||
        (f.posts_count > 0 && !(f.previous_month_posts_count ?? 0)) ||
        (f.members_count > 0 && !(f.previous_month_members_count ?? 0))
    );
    if (needsBaseline.length === 0) {
      toast.success('Tüm forumlarda önceki ay bazı zaten tanımlı');
      return;
    }
    if (
      !confirm(
        `${needsBaseline.length} forumda mevcut sayılar "önceki ay" bazına yazılacak (şimdilik fark 0 görünür; bir sonraki scrape gerçek artışı gösterir). Devam?`
      )
    ) {
      return;
    }
    setLoading(true);
    let ok = 0;
    try {
      for (const forum of needsBaseline) {
        const patch: Record<string, number> = {};
        if (forum.topics_count > 0 && !(forum.previous_month_topics_count ?? 0)) {
          patch.previous_month_topics_count = forum.topics_count;
        }
        if (forum.posts_count > 0 && !(forum.previous_month_posts_count ?? 0)) {
          patch.previous_month_posts_count = forum.posts_count;
        }
        if (forum.members_count > 0 && !(forum.previous_month_members_count ?? 0)) {
          patch.previous_month_members_count = forum.members_count;
        }
        if (Object.keys(patch).length === 0) continue;
        const { error } = await supabase.from('forums').update(patch).eq('id', forum.id);
        if (error) throw error;
        ok++;
      }
      toast.success(`${ok} forum için önceki ay bazı oluşturuldu`);
      await fetchForums();
    } catch (e) {
      console.error(e);
      toast.error('Baz oluşturulurken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForums();
    fetchCategories();
  }, []);

  const debouncedSearch = useMemo(
    () => debounce((term: string) => {
      setSearchTerm(term);
      setCurrentPage(1);
    }, 300),
    []
  );

  useEffect(() => {
    let result = [...forums];

    if (searchTerm) {
      result = result.filter(forum => 
        forum.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        forum.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (!showInactive) {
      result = result.filter(forum => forum.is_active);
    }

    result = result.filter(forum => {
      const matchesCategory = !filters.category || forum.category === filters.category;
      const matchesYear = forum.founded_year >= filters.yearRange[0] && forum.founded_year <= filters.yearRange[1];
      const matchesMembers = forum.members_count >= filters.minMembers;
      const matchesPosts = forum.posts_count >= filters.minPosts;
      return matchesCategory && matchesYear && matchesMembers && matchesPosts;
    });

    result.sort((a, b) => {
      if (sortConfig.key === 'updated_at') {
        const aMs = new Date(lastUpdatedSource(a) || 0).getTime();
        const bMs = new Date(lastUpdatedSource(b) || 0).getTime();
        return sortConfig.direction === 'asc' ? aMs - bMs : bMs - aMs;
      }

      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue, 'tr')
          : bValue.localeCompare(aValue, 'tr');
      }

      return sortConfig.direction === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    setFilteredForums(result);
  }, [forums, searchTerm, showInactive, sortConfig, filters]);

  const fetchForums = async () => {
    try {
      const { data, error } = await supabase
        .from('forums')
        .select('*')
        .order('name');

      if (error) throw error;
      setForums(data || []);
    } catch (error) {
      console.error('Error fetching forums:', error);
      toast.error('Forumlar yüklenirken bir hata oluştu');
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Kategoriler yüklenirken bir hata oluştu');
    }
  };

  const handleSort = (key: keyof Forum) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const resetForumForm = () => {
    setFormData(createEmptyForumForm());
    setSelectedForum(null);
    setPendingSuggestionId(null);
    storedScrapePasswordRef.current = '';
  };

  const openAddModal = () => {
    resetForumForm();
    setIsModalOpen(true);
  };

  const closeForumModal = () => {
    setIsModalOpen(false);
    resetForumForm();
  };
  const openAddModalFromSuggestion = (prefill: SuggestionPrefillPayload) => {
    setSelectedForum(null);
    setPendingSuggestionId(prefill.suggestionId);
    setFormData(suggestionPrefillToForumForm(prefill));
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!isModalOpen || selectedForum || pendingSuggestionId) return;
    setFormData((prev) =>
      prev.logo_url.trim() ? prev : { ...prev, logo_url: DEFAULT_FORUM_LOGO_URL }
    );
  }, [isModalOpen, selectedForum, pendingSuggestionId]);

  useEffect(() => {
    const state = location.state as LocationSuggestionState | null;
    if (!state?.suggestionPrefill || suggestionHandledRef.current) return;
    suggestionHandledRef.current = true;
    openAddModalFromSuggestion(state.suggestionPrefill);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const handleAdd = () => {
    openAddModal();
  };

  const handleEdit = (forum: Forum) => {
    setSelectedForum(forum);
    storedScrapePasswordRef.current = forum.scrape_password ?? '';
    setFormData({
      forum_id: forum.forum_id,
      name: forum.name,
      category: forum.category,
      url: forum.url || '',
      topics_count: forum.topics_count || 0,
      posts_count: forum.posts_count || 0,
      members_count: forum.members_count || 0,
      founded_year: forum.founded_year || new Date().getFullYear(),
      description: forum.description || '',
      logo_url: forum.logo_url || '',
      is_active: forum.is_active,
      software: forum.software || '',
      is_sponsored: forum.is_sponsored || false,
      sponsored_until: forum.sponsored_until || '',
      topics_selector: forum.topics_selector || '',
      posts_selector: forum.posts_selector || '',
      members_selector: forum.members_selector || '',
      scrape_login_url: forum.scrape_login_url || '',
      scrape_username: forum.scrape_username || '',
      scrape_password: '',
      scrape_login_username_selector: forum.scrape_login_username_selector || '',
      scrape_login_password_selector: forum.scrape_login_password_selector || '',
      scrape_login_submit_selector: forum.scrape_login_submit_selector || '',
      promo_section_url: forum.promo_section_url || '',
      forum_admins: (() => {
        const list = resolveForumAdmins(forum);
        return list.length > 0 ? list : [emptyForumAdminRow()];
      })(),
      forum_controllers: (() => {
        const list = resolveForumControllers(forum);
        return list.length > 0 ? list : [emptyForumControllerRow()];
      })(),
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Bu forumu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('forums')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setForums(forums.filter(forum => forum.id !== id));
      toast.success('Forum başarıyla silindi');
    } catch (error) {
      console.error('Error deleting forum:', error);
      toast.error('Forum silinirken bir hata oluştu');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const nextCounts = {
        topics_count: Number(formData.topics_count),
        posts_count: Number(formData.posts_count),
        members_count: Number(formData.members_count),
      };
      const previousValues = selectedForum
        ? buildPreviousMonthPatch(selectedForum, nextCounts)
        : {};

      // Otomatik bakım: eksik önceki-ay bazını doldur + açıklamayı güncel sayılara göre yenile.
      const mergedPrevMonth = {
        previous_month_topics_count:
          previousValues.previous_month_topics_count ?? selectedForum?.previous_month_topics_count ?? null,
        previous_month_posts_count:
          previousValues.previous_month_posts_count ?? selectedForum?.previous_month_posts_count ?? null,
        previous_month_members_count:
          previousValues.previous_month_members_count ?? selectedForum?.previous_month_members_count ?? null,
      };
      // Admin açıklamayı elle değiştirdiyse dokunma; aksi halde otomatik yeniden üret.
      const adminEditedDescription = selectedForum
        ? formData.description !== (selectedForum.description ?? '')
        : formData.description.trim() !== '';
      const autoMaintenancePatch = buildForumAutoMaintenancePatch({
        counts: nextCounts,
        prevMonth: mergedPrevMonth,
        forum: {
          forum_id: formData.forum_id,
          name: formData.name,
          category: formData.category,
          ...nextCounts,
        },
        currentDescription: selectedForum?.description,
        regenerateDescription: !adminEditedDescription,
      });

      let sponsoredUntil = null;
      if (formData.is_sponsored && formData.sponsored_until) {
        sponsoredUntil = formData.sponsored_until;
      }

      const scrapePasswordInput = formData.scrape_password?.trim() ?? '';
      const scrapePassword =
        scrapePasswordInput !== ''
          ? scrapePasswordInput
          : selectedForum
            ? storedScrapePasswordRef.current || null
            : null;

      const updatedData = {
        ...formData,
        ...nextCounts,
        logo_url: selectedForum ? formData.logo_url : resolveForumLogoUrl(formData.logo_url),
        founded_year: Number(formData.founded_year),
        software: formData.software || null,
        sponsored_until: sponsoredUntil,
        topics_selector: formData.topics_selector?.trim() || null,
        posts_selector: formData.posts_selector?.trim() || null,
        members_selector: formData.members_selector?.trim() || null,
        scrape_login_url: formData.scrape_login_url?.trim() || null,
        scrape_username: formData.scrape_username?.trim() || null,
        scrape_password: scrapePassword,
        scrape_login_username_selector: formData.scrape_login_username_selector?.trim() || null,
        scrape_login_password_selector: formData.scrape_login_password_selector?.trim() || null,
        scrape_login_submit_selector: formData.scrape_login_submit_selector?.trim() || null,
        promo_section_url: formData.promo_section_url?.trim() || null,
        forum_admins: normalizeForumAdmins(formData.forum_admins),
        forum_controllers: normalizeForumContacts(formData.forum_controllers),
        forum_admin_name: null,
        forum_admin_profile_url: null,
        user_id: user?.id || null,
        updated_at: new Date().toISOString(),
        ...previousValues,
        ...autoMaintenancePatch,
      };

      if (selectedForum) {
        const { error } = await supabase
          .from('forums')
          .update(updatedData)
          .eq('id', selectedForum.id);

        if (error) throw error;
        if (scrapePasswordInput) {
          storedScrapePasswordRef.current = scrapePasswordInput;
        }
        await recordForumStatSnapshot(
          supabase,
          { id: selectedForum.id, ...nextCounts },
          updatedData.updated_at
        );
        setForums(forums.map(f => f.id === selectedForum.id ? { ...f, ...updatedData, scrape_password: scrapePassword } : f));
        toast.success('Forum başarıyla güncellendi');
      } else {
        const { data, error } = await supabase
          .from('forums')
          .insert([
            {
              ...updatedData,
              created_at: new Date().toISOString(),
            },
          ])
          .select()
          .single();

        if (error) throw error;
        if (scrapePasswordInput) {
          storedScrapePasswordRef.current = scrapePasswordInput;
        } else if (data.scrape_password) {
          storedScrapePasswordRef.current = data.scrape_password;
        }
        await recordForumStatSnapshot(
          supabase,
          {
            id: data.id,
            topics_count: data.topics_count,
            posts_count: data.posts_count,
            members_count: data.members_count,
          },
          data.created_at
        );
        setForums([...forums, data]);
        toast.success('Forum başarıyla eklendi');

        if (pendingSuggestionId) {
          await supabase
            .from('forum_suggestions')
            .update({
              status: 'approved',
              admin_notes: `Listeye eklendi: ${data.forum_id} (${data.name})`,
            })
            .eq('id', pendingSuggestionId);
          setPendingSuggestionId(null);
        }
      }

      closeForumModal();
    } catch (error) {
      console.error('Error saving forum:', error);
      toast.error('Forum kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(filteredForums.length / ITEMS_PER_PAGE);
  const paginatedForums = filteredForums.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div>
      <ForumListToolbar
        bulkScrapeScope={bulkScrapeScope}
        onBulkScrapeScopeChange={setBulkScrapeScope}
        bulkScraping={bulkScraping}
        updatingDescriptions={updatingDescriptions}
        loading={loading}
        onBulkScrape={handleBulkScrapeFromSelectors}
        onUpdateDescriptions={handleUpdateDescriptions}
        onEstablishBaselines={handleEstablishStatBaselines}
        onFillSnapshots={handleFillAllSnapshots}
        onRollback={handleRollbackLastBulk}
        onExportCsv={() => exportData('csv')}
        onExportTxt={() => exportData('txt')}
        onAddForum={handleAdd}
      />

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
        <div className="p-4 border-b dark:border-gray-700">
          <div className="flex items-center justify-between gap-4">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Forum ara..."
                onChange={(e) => debouncedSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex gap-4">
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="px-4 py-2 rounded-lg border dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Tüm Kategoriler</option>
                {categories.map(category => (
                  <option key={category.id} value={category.name}>{category.name}</option>
                ))}
              </select>

              <input
                type="number"
                placeholder="Min. Üye"
                value={filters.minMembers || ''}
                onChange={(e) => setFilters({ ...filters, minMembers: parseInt(e.target.value) || 0 })}
                className="px-4 py-2 rounded-lg border dark:border-gray-600 dark:bg-gray-700 dark:text-white w-32"
              />

              <input
                type="number"
                placeholder="Min. İleti"
                value={filters.minPosts || ''}
                onChange={(e) => setFilters({ ...filters, minPosts: parseInt(e.target.value) || 0 })}
                className="px-4 py-2 rounded-lg border dark:border-gray-600 dark:bg-gray-700 dark:text-white w-32"
              />

              <button
                onClick={() => setShowInactive(!showInactive)}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {showInactive ? (
                  <ToggleRight className="text-indigo-500" size={20} />
                ) : (
                  <ToggleLeft className="text-gray-400" size={20} />
                )}
                {showInactive ? 'Tüm Forumlar' : 'Sadece Aktif'}
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b dark:border-gray-700">
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
                  onClick={() => handleSort('name')}
                >
                  Forum Adı
                </th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
                  onClick={() => handleSort('category')}
                >
                  Kategori
                </th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
                  onClick={() => handleSort('software')}
                >
                  Yazılım
                </th>
                <th
                  className="px-6 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white"
                  title="En az bir istatistik CSS seçicisi tanımlı mı?"
                >
                  CSS Seçici
                </th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
                  onClick={() => handleSort('members_count')}
                >
                  Üye Sayısı
                </th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
                  onClick={() => handleSort('posts_count')}
                >
                  İleti Sayısı
                </th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
                  onClick={() => handleSort('topics_count')}
                >
                  Konu Sayısı
                </th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
                  onClick={() => handleSort('founded_year')}
                >
                  Kuruluş Yılı
                </th>
                <th
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer whitespace-nowrap"
                  onClick={() => handleSort('updated_at')}
                >
                  Son güncelleme
                </th>
                <th 
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
                  onClick={() => handleSort('is_active')}
                >
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedForums.map(forum => (
                <tr key={forum.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{forum.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{forum.category}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{forum.software || '-'}</td>
                  <td className="px-6 py-4">
                    {forumHasAnySelector(forum) ? (
                      <span
                        className="inline-flex items-center gap-1.5 text-green-600 dark:text-green-400"
                        title="En az bir CSS seçici tanımlı"
                      >
                        <CheckCircle2 size={18} aria-hidden />
                        <span className="text-xs font-medium">Var</span>
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1.5 text-gray-400 dark:text-gray-500"
                        title="CSS seçici tanımlı değil"
                      >
                        <XCircle size={18} aria-hidden />
                        <span className="text-xs font-medium">Yok</span>
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{forum.members_count.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{forum.posts_count.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{forum.topics_count.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{forum.founded_year}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {formatLastUpdatedAdmin(lastUpdatedSource(forum))}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      forum.is_active 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {forum.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(forum)}
                        className="p-1 text-blue-600 hover:text-blue-800"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(forum.id)}
                        className="p-1 text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Toplam {filteredForums.length} forum
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-sm">
              Sayfa {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      <BulkScrapeProgressDialog progress={bulkProgress} />
      <BulkScrapeReportDialog
        key={bulkScrapeReport?.finishedAt.getTime() ?? 'bulk-report'}
        open={bulkScrapeReportOpen}
        report={bulkScrapeReport}
        saving={bulkScrapeSaving}
        onClose={handleCloseBulkScrapeReport}
        onConfirmSave={handleConfirmBulkScrapeSave}
      />

      <Dialog
        open={isModalOpen}
        onClose={closeForumModal}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen">
          <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

          <div className="relative bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {selectedForum ? 'Forum Düzenle' : pendingSuggestionId ? 'Yeni Forum Ekle (öneriden)' : 'Yeni Forum Ekle'}
            </Dialog.Title>
            {pendingSuggestionId && !selectedForum && (
              <p className="text-sm text-indigo-600 dark:text-indigo-400 mb-4">
                Forum önerisinden gelen bilgiler dolduruldu. Kaydettiğinizde öneri onaylanmış sayılır.
              </p>
            )}

            <form
              key={selectedForum?.id ?? pendingSuggestionId ?? 'new-forum'}
              onSubmit={handleSave}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Forum ID
                </label>
                <input
                  type="text"
                  value={formData.forum_id}
                  onChange={(e) => setFormData((prev) => ({ ...prev, forum_id: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Forum Adı
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kategori
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                >
                  <option value="">Kategori Seçin</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Forum Yazılımı
                </label>
                <input
                  type="text"
                  value={formData.software || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, software: e.target.value }))}
                  list="software-list"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Yazılım adı girin..."
                />
                <datalist id="software-list">
                  <option value="vBulletin" />
                  <option value="XenForo" />
                  <option value="phpBB" />
                  <option value="MyBB" />
                  <option value="SMF" />
                  <option value="Discourse" />
                  <option value="NodeBB" />
                  <option value="Invision Community" />
                  <option value="Custom" />
                  <option value="Other" />
                </datalist>
              </div>

              <div className="rounded-lg border-2 border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-950/30 p-4 space-y-3">
                <div className="text-sm font-semibold text-teal-900 dark:text-teal-200">
                  Tanıtım, Admin &amp; Controller
                </div>
                <p className="text-xs text-teal-800/80 dark:text-teal-300/80 leading-relaxed">
                  Forum kartının sağındaki çıkıntılarda yalnızca forumadmin girişinde görünür.
                  Birden fazla admin veya controller ekleyebilirsiniz.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tanıtım bölümü linki
                  </label>
                  <input
                    type="url"
                    value={formData.promo_section_url ?? ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, promo_section_url: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                    placeholder="https://forum.example.com/forumdisplay.php?f=12"
                  />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Forum adminleri
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          forum_admins: [...(prev.forum_admins ?? []), emptyForumAdminRow()],
                        }))
                      }
                      className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-teal-700"
                    >
                      <Plus size={14} /> Yönetici ekle
                    </button>
                  </div>
                  {(formData.forum_admins?.length ? formData.forum_admins : [emptyForumAdminRow()]).map(
                    (admin, index) => (
                      <div
                        key={index}
                        className="rounded-lg border border-teal-200/80 dark:border-teal-800 bg-white/70 dark:bg-gray-800/60 p-3 space-y-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            #{index + 1}
                          </span>
                          {(formData.forum_admins?.length ?? 0) > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  forum_admins: (prev.forum_admins ?? []).filter((_, i) => i !== index),
                                }))
                              }
                              className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={13} /> Kaldır
                            </button>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Ad
                          </label>
                          <input
                            type="text"
                            value={admin.name ?? ''}
                            onChange={(e) =>
                              setFormData((prev) => {
                                const list = [...(prev.forum_admins ?? [emptyForumAdminRow()])];
                                list[index] = { ...list[index], name: e.target.value };
                                return { ...prev, forum_admins: list };
                              })
                            }
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            placeholder="Örn. SiteYöneticisi"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Profil linki
                          </label>
                          <input
                            type="url"
                            value={admin.profile_url ?? ''}
                            onChange={(e) =>
                              setFormData((prev) => {
                                const list = [...(prev.forum_admins ?? [emptyForumAdminRow()])];
                                list[index] = { ...list[index], profile_url: e.target.value };
                                return { ...prev, forum_admins: list };
                              })
                            }
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            placeholder="https://forum.example.com/member.php?u=1"
                          />
                        </div>
                      </div>
                    )
                  )}
                </div>
                <div className="space-y-3 pt-1 border-t border-teal-200/60 dark:border-teal-800/60">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Controller (kontrol eden)
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          forum_controllers: [
                            ...(prev.forum_controllers ?? []),
                            emptyForumControllerRow(),
                          ],
                        }))
                      }
                      className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-teal-700"
                    >
                      <Plus size={14} /> Controller ekle
                    </button>
                  </div>
                  {(formData.forum_controllers?.length
                    ? formData.forum_controllers
                    : [emptyForumControllerRow()]
                  ).map((controller, index) => (
                    <div
                      key={index}
                      className="rounded-lg border border-teal-200/80 dark:border-teal-800 bg-white/70 dark:bg-gray-800/60 p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          #{index + 1}
                        </span>
                        {(formData.forum_controllers?.length ?? 0) > 1 && (
                          <button
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                forum_controllers: (prev.forum_controllers ?? []).filter(
                                  (_, i) => i !== index
                                ),
                              }))
                            }
                            className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                          >
                            <Trash2 size={13} /> Kaldır
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Ad
                        </label>
                        <input
                          type="text"
                          value={controller.name ?? ''}
                          onChange={(e) =>
                            setFormData((prev) => {
                              const list = [
                                ...(prev.forum_controllers ?? [emptyForumControllerRow()]),
                              ];
                              list[index] = { ...list[index], name: e.target.value };
                              return { ...prev, forum_controllers: list };
                            })
                          }
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                          placeholder="Örn. KontrolEden"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Profil linki
                        </label>
                        <input
                          type="url"
                          value={controller.profile_url ?? ''}
                          onChange={(e) =>
                            setFormData((prev) => {
                              const list = [
                                ...(prev.forum_controllers ?? [emptyForumControllerRow()]),
                              ];
                              list[index] = { ...list[index], profile_url: e.target.value };
                              return { ...prev, forum_controllers: list };
                            })
                          }
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                          placeholder="https://forum.example.com/member.php?u=2"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedForum && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-900/40">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Son güncelleme</p>
                  <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">
                    {formatLastUpdatedAdmin(lastUpdatedSource(selectedForum))}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Kayıt veya scrape sonrası otomatik güncellenir.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  URL
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData((prev) => ({ ...prev, url: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div className="rounded-lg border border-amber-100 dark:border-amber-900/40 bg-amber-50/50 dark:bg-gray-900/40 p-4 space-y-3">
                <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  Scrape girişi (isteğe bağlı)
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  İstatistikler yalnızca üye oturumunda görünüyorsa giriş bilgilerini doldurun. Scrape önce bu
                  seçicilerle giriş yapar, ardından istatistik seçicilerini okur. Tüm giriş alanları dolu olmalı;
                  boş bırakırsanız anonim ziyaretçi gibi çekilir.
                </p>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Giriş sayfası URL
                  </label>
                  <input
                    type="url"
                    value={formData.scrape_login_url}
                    onChange={(e) => setFormData((prev) => ({ ...prev, scrape_login_url: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                    placeholder="https://forum.example.com/login.php"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Kullanıcı adı
                    </label>
                    <input
                      type="text"
                      autoComplete="off"
                      value={formData.scrape_username}
                      onChange={(e) => setFormData((prev) => ({ ...prev, scrape_username: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Şifre
                    </label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={formData.scrape_password}
                      onChange={(e) => setFormData((prev) => ({ ...prev, scrape_password: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                      placeholder={
                        selectedForum && storedScrapePasswordRef.current
                          ? 'Kayıtlı şifre korunur — değiştirmek için yazın'
                          : ''
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Kullanıcı adı alanı seçici
                  </label>
                  <input
                    type="text"
                    value={formData.scrape_login_username_selector}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, scrape_login_username_selector: e.target.value }))
                    }
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm"
                    placeholder="#login_username"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Şifre alanı seçici
                  </label>
                  <input
                    type="text"
                    value={formData.scrape_login_password_selector}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, scrape_login_password_selector: e.target.value }))
                    }
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm"
                    placeholder="#login_password"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Giriş butonu seçici
                  </label>
                  <input
                    type="text"
                    value={formData.scrape_login_submit_selector}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, scrape_login_submit_selector: e.target.value }))
                    }
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm"
                    placeholder="input[type=submit]"
                  />
                </div>
              </div>

              <div className="rounded-lg border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/50 dark:bg-gray-900/40 p-4 space-y-3">
                <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  İstatistik CSS seçicileri
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  Her alan ayrı eleman ise normal CSS seçici yeterli. Konu / ileti / üye aynı satırda
                  ise ortak seçiciye <code className="font-mono text-indigo-700 dark:text-indigo-300">{' >> Etiket'}</code>{' '}
                  ekleyin — örn.{' '}
                  <code className="font-mono text-[11px] break-all">#collapseobj_forumhome_stats .smallfont div {'>> Konular'}</code>
                  . <code className="font-mono text-[11px]">57 İleti</code> gibi metinlerde sayı etiketten önce;
                  {' '}
                  <code className="font-mono text-[11px]">Mesajlar: 1.494.916</code> gibi metinlerde etiketten sonra (iki nokta ile) okunur.
                  Birden fazla üye satırı varsa tam etiketi yazın (ör. <code className="font-mono text-[11px]">{'>> Kayıtlı Üye'}</code>).
                </p>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Konu sayısı seçici
                  </label>
                  <input
                    type="text"
                    value={formData.topics_selector}
                    onChange={(e) => setFormData((prev) => ({ ...prev, topics_selector: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm"
                    placeholder="#stats .smallfont div >> Konular"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    İleti sayısı seçici
                  </label>
                  <input
                    type="text"
                    value={formData.posts_selector}
                    onChange={(e) => setFormData((prev) => ({ ...prev, posts_selector: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm"
                    placeholder="#upshrink_stats .stats_flex >> İleti"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Üye sayısı seçici
                  </label>
                  <input
                    type="text"
                    value={formData.members_selector}
                    onChange={(e) => setFormData((prev) => ({ ...prev, members_selector: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm"
                    placeholder="#stats .smallfont div >> Üye"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleScrapeFromSelectorsInModal}
                  disabled={scrapingModal}
                  className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2"
                >
                  <RefreshCw size={16} className={scrapingModal ? 'animate-spin' : ''} />
                  {scrapingModal ? 'Çekiliyor...' : 'Veri güncelle (seçicilerden oku)'}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Konu Sayısı
                  </label>
                  <input
                    type="number"
                    key={`stat-topics-${statCountInputNonce}`}
                    value={formData.topics_count}
                    onChange={(e) => setFormData((prev) => ({ ...prev, topics_count: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    İleti Sayısı
                  </label>
                  <input
                    type="number"
                    key={`stat-posts-${statCountInputNonce}`}
                    value={formData.posts_count}
                    onChange={(e) => setFormData((prev) => ({ ...prev, posts_count: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    min="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Üye Sayısı
                  </label>
                  <input
                    type="number"
                    key={`stat-members-${statCountInputNonce}`}
                    value={formData.members_count}
                    onChange={(e) => setFormData((prev) => ({ ...prev, members_count: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Kuruluş Yılı
                  </label>
                  <input
                    type="number"
                    value={formData.founded_year}
                    onChange={(e) => setFormData((prev) => ({ ...prev, founded_year: parseInt(e.target.value) || new Date().getFullYear() }))}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    min="1990"
                    max={new Date().getFullYear()}
                  />
                </div>
              </div>

              <ForumScorePreview
                topics={formData.topics_count}
                posts={formData.posts_count}
                members={formData.members_count}
                foundedYear={formData.founded_year}
                savedScore={
                  selectedForum
                    ? calculateForumScore(
                        selectedForum.topics_count || 0,
                        selectedForum.posts_count || 0,
                        selectedForum.members_count || 0,
                        selectedForum.founded_year || new Date().getFullYear()
                      )
                    : null
                }
              />

              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Açıklama
                  </label>
                  <button
                    type="button"
                    onClick={handleRegenerateDescription}
                    disabled={regeneratingDescription}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-900/30 disabled:opacity-50"
                  >
                    <RefreshCw size={14} className={regeneratingDescription ? 'animate-spin' : ''} />
                    {regeneratingDescription ? 'Oluşturuluyor…' : 'Açıklamayı güncelle'}
                  </button>
                </div>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Mevcut istatistiklere göre SEO açıklaması üretir; kaydetmeden veritabanına yazılmaz.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Logo URL
                </label>
                <input
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => setFormData((prev) => ({ ...prev, logo_url: e.target.value }))}
                  placeholder={DEFAULT_FORUM_LOGO_URL}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <ForumLogoPreview url={formData.logo_url} name={formData.name} />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  Aktif
                </label>
              </div>

              <div className="space-y-4 border-t pt-4 dark:border-gray-700">
                <h3 className="text-lg font-semibold">Sponsorluk Ayarları</h3>
                
                <div className="flex items-center gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_sponsored"
                      checked={formData.is_sponsored}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          is_sponsored: e.target.checked,
                          sponsored_until: e.target.checked ? prev.sponsored_until : '',
                        }));
                      }}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_sponsored" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Sponsorlu Forum
                    </label>
                  </div>

                  {formData.is_sponsored && (
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Sponsorluk Bitiş Tarihi
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.sponsored_until || ''}
                        onChange={(e) => setFormData((prev) => ({ ...prev, sponsored_until: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        required={formData.is_sponsored}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeForumModal}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {loading ? 'Kaydediliyor...' : (selectedForum ? 'Güncelle' : 'Ekle')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ForumList;